import {createRouter, createWebHistory} from 'vue-router';
import UserService from '@/service/backend/user.service'
import { store } from '@/store';

const routes = [
    {
        path: '/login',
        name: 'Login',
        meta: { public: true, onlyWhenLoggedOut: true },
        component: () => import('./pages/Login.vue')
    },
    {
        path: '/reset-password',
        name: 'ResetPassword',
        meta: { public: true, onlyWhenLoggedOut: true },
        component: () => import('./pages/ResetPassword.vue')
    },
    {
        path: '/reset-password/:token',
        name: 'ResetPasswordToken',
        meta: { public: true, onlyWhenLoggedOut: true },
        component: () => import('./pages/ResetPassword.vue')
    },
    {
        path: '/',
        component: () => import('./App.vue'),
        children: [
            {
                path: '/kezdes',
                name: 'dashboard',
                meta: { roles: ['ADMIN', 'CARER'] },
                exact: true,
                component: () => import('./components/Dashboard.vue')
            },
            { 
                path: '/', 
                component: () => import('./components/Dashboard.vue'), 
                alias: '/kezdes' 
            },
            {
                path: '/profil',
                name: 'profile',
                exact: true,
                component: () => import('./components/UserProfile.vue')
            },
            {
                path: '/beteg/:patientId',
                name: 'patient',
                meta: { roles: ['ADMIN', 'CARER', 'MANAGER'] },
                component: () => import('./pages/otthonapolo/DetailsPatient.vue'),
            },
            {
                path: '/beteg',
                name: 'patients',
                meta: { roles: ['ADMIN', 'CARER', 'MANAGER'] },
                component: () => import('./pages/otthonapolo/CrudPatients.vue'),
            },
            {
                path: '/rendeles',
                name: 'orders',
                component: () => import('./pages/otthonapolo/CrudOrders.vue'),
            },
            {
                path: '/vizitkerelem',
                name: 'visitrequests',
                meta: { roles: ['ADMIN', 'CARER'] },
                component: () => import('./pages/otthonapolo/CrudVisitRequests.vue')
            },
            {
                path: '/teljesitesek',
                name: 'usercosts',
                meta: { roles: ['ADMIN'] },
                component: () => import('./pages/otthonapolo/CrudUsercosts.vue'),
            },
            {
                path: '/teljesites',
                name: 'usercost',
                meta: { roles: ['CARER'] },
                component: () => import('./pages/otthonapolo/PageUsercost.vue'),
            },
            {
                path: '/riport/teljesites',
                name: 'riport-compliance',
                meta: { roles: ['ADMIN'] },
                component: () => import('./pages/otthonapolo/ReportCompliance.vue')
            },
            {
                path: '/riport/rendeles',
                name: 'riport-orders',
                meta: { roles: ['ADMIN'] },
                component: () => import('./pages/otthonapolo/ReportOrders.vue')
            },
            {
                path: '/riport/ujbetegek',
                name: 'riport-new-patients',
                meta: { roles: ['ADMIN'] },
                component: () => import('./pages/otthonapolo/ReportNewPatients.vue')
            },
            {
                path: '/felhasznalo',
                name: 'users',
                meta: { roles: ['ADMIN'] },
                component: () => import('./pages/otthonapolo/CrudUsers.vue')
            },
            {
                path: '/orvos',
                name: 'doctors',
                meta: { roles: ['ADMIN', 'CARER'] },
                component: () => import('./pages/otthonapolo/CrudDoctors.vue')
            },
            {
                path: '/korhaz',
                name: 'hospitals',
                meta: { roles: ['ADMIN'] },
                component: () => import('./pages/otthonapolo/CrudHospitals.vue')
            },
            {
                path: '/tapszer',
                name: 'nutritions',
                meta: { roles: ['ADMIN'] },
                component: () => import('./pages/otthonapolo/CrudNutritions.vue')
            },
            {
                path: '/eszkoz',
                name: 'tools',
                meta: { roles: ['ADMIN'] },
                component: () => import('./pages/otthonapolo/CrudTools.vue')
            },
            {
                path: '/vizittipus',
                name: 'visittypes',
                meta: { roles: ['ADMIN'] },
                component: () => import('./pages/otthonapolo/CrudVisitTypes.vue')
            },
            {
                path: '/koltseg',
                name: 'costs',
                meta: { roles: ['ADMIN'] },
                component: () => import('./pages/otthonapolo/CrudCosts.vue')
            },
            {
                path: '/kisterseg',
                name: 'areas',
                meta: { roles: ['ADMIN'] },
                component: () => import('./pages/otthonapolo/CrudAreas.vue')
            },
            {
                path: '/telepules',
                name: 'subareas',
                meta: { roles: ['ADMIN'] },
                component: () => import('./pages/otthonapolo/CrudSubareas.vue')
            },
            {
                path: '/parameter',
                name: 'parameter',
                meta: { roles: ['ADMIN'] },
                component: () => import('./pages/otthonapolo/PageParameter.vue')
            },
        ]
    },
    {
        path: '/error',
        name: 'error',
        component: () => import('./pages/Error.vue')
    },
    {
        path: '/notfound',
        name: 'notfound',
        component: () => import('./pages/NotFound.vue')
    },
    {
        path: '/logout',
        name: 'logout',
        component: () => import('./pages/Logout.vue')
    },
];

const router = createRouter({
    history: createWebHistory(),
    base: process.env.BASE_URL,
    routes,
    scrollBehavior(to, from, savedPosition) {
        if (savedPosition) {
          return savedPosition
        } else {
          return { top: 0 }
        }
      },
});

router.beforeEach(async (to, from, next) => {

    // Sanitize any redirect parameters
    if (to.query.redirect) {
        const redirect = to.query.redirect
        
        // Only allow relative URLs that begin with / and contain no HTML
        if (typeof redirect === 'string' && 
            redirect.startsWith('/') && 
            !/[<>'"&]/.test(redirect) &&
            !redirect.includes('javascript:')) {
        // Redirect is safe, proceed - continue with other checks
        } else {
        // Remove unsafe redirect parameter
        const sanitizedQuery = { ...to.query }
        delete sanitizedQuery.redirect
        
        return next({ 
            path: to.path, 
            query: sanitizedQuery,
            hash: to.hash 
        })
        }
    }
    
    const isPublic = to.matched.some(record => record.meta.public)
    const onlyWhenLoggedOut = to.matched.some(record => record.meta.onlyWhenLoggedOut)
    const loggedIn = await UserService.loggedIn()

    if (!isPublic && !loggedIn && to.path != '/login') {
        let nextPage = { path: '/login' }
        if(to.fullPath !== "/"){
            nextPage.query = { redirect: to.fullPath } // Store the full path to redirect the user to after login
        } 
        return next(nextPage);
    }
  
    // Do not allow user to visit login page or register page if they are logged in
    if (loggedIn && onlyWhenLoggedOut) {
      return next('/')
    }

    if (loggedIn) {
        let User = store.getters['user/User']
        let Userrole = User.role
        const pathHasRole = to.matched.some(record => record.meta.roles)
        if(to.path == '/' && Userrole === "PHARMACY")
            return next('/rendeles')
        if(to.path == '/' && (Userrole === "ADMIN" || Userrole === "CARER" || Userrole === "MANAGER"))
            return next('/beteg')

        let hasRight = true
        if(pathHasRole){
            hasRight = to.matched.some(record => (record.meta.roles == undefined) ? false : record.meta.roles.includes(Userrole), Userrole)
        }
        if(!hasRight)
            return next('/notfound')
    }


    next()

})

export default router;

/*
    scrollBehavior (to, from, savedPosition) {
        if (savedPosition) {
          return savedPosition
        } else {
          return { x: 0, y: 0 }
        }
    }
*/